import {
    CloseOutlined,
    FilterAltOffOutlined,
    FilterAltOutlined,
} from "@mui/icons-material";
import {
    Alert,
    Box,
    Button,
    Card,
    Grid,
    IconButton,
    TextField,
    Typography,
    useMediaQuery,
    useTheme,
} from "@mui/material";
import { useQuery } from "@tanstack/react-query";
import { useEffect, useMemo, useState } from "react";
import { GET } from "../../../Utilities/BaseService";
import { MultiPermissionAuthorize } from "../../../Utilities/MultiPermissionAuthorize";
import UpdatedSearchableInput from "../../../Utilities/UpdatedSearchableInput";
import BaseTable from "../../Table/BaseTable";
import SearchBar from "../../Table/Components/SearchBar";
import { currencyFormatter } from "../Bill/Cells/Last12Summary";
import { MonthlyDefaulterBreakdown } from "./ArrearsReportDetail";
import ArrearsReportDetailNew from "./ArrearsReportDetailNew";

type Filter = {
    search: string;
    district: number | "" | "all";
    village: number | "" | "all";
    hamlet: number | "" | "all";
    connectionType: number | "" | "all";
    startDate: any;
    endDate: any;
    powerstation: string;
    month: any;
    year: any;
};

const initialFilter: Filter = {
    search: "",
    district: "",
    village: "",
    hamlet: "",
    startDate: "",
    endDate: "",
    connectionType: "",
    powerstation: "",
    month: "",
    year: "",
};

const initialClearAll = {
    district: false,
    village: false,
    hamlet: false,
    powerstation: false,
    connectionType: false,
    search: false,
};

export const getArrearsReport = (pagination: any, filter: any) => {
    return GET("/reading/defaulters-report-against-reading", {
        meterNo: filter.search ? filter.search : "",
        district:
            filter.district && filter.district !== "all"
                ? filter.district
                : undefined,
        village:
            filter.village && filter.village !== "all"
                ? filter.village
                : undefined,
        hamlet:
            filter.hamlet && filter.hamlet !== "all"
                ? filter.hamlet
                : undefined,

        startDate: filter.startDate ? filter.startDate : undefined,
        endDate: filter.endDate ? filter.endDate : undefined,
        connectionType: filter["connection type"],
        powerstation: filter.powerstation ? filter.powerstation : undefined,
        month: filter.month,
        year: filter.year,
        "connection type": undefined,
        page: pagination.page + 1,
        limit: pagination.limit,
    });
};

const ArrearsReportNew = () => {
    const currentMonth = new Date().toISOString().slice(0, 7); // YYYY-MM
    const [pagination, setPagination] = useState<any>({ page: 0, limit: 100 });
    const [billingMonth, setBillingMonth] = useState("");
    const [clearAll, setClearAll] = useState(initialClearAll);

    const [filter, setFilter] = useState<any>(initialFilter);
    const [feedback, setFeedback] = useState("");
    const [errFeedback, setErrFeedback] = useState<any>({});

    const [changeCount, setChangeCount] = useState(0);
    const [rowsCount, setRowsCount] = useState(0);
    const theme = useTheme();
    const mdDown = useMediaQuery(theme.breakpoints.down("md"));
    // const [intermediateFilter, setIntermediateFilter] =
    //     useState<Filter>(initialFilter);

    // const [clearAll, setClearAll] = useState(initialClearAll);

    const { data, isLoading } = useQuery(
        ["arrears", pagination.page, pagination.limit, changeCount],
        () => getArrearsReport(pagination, filter),
        {
            enabled: changeCount > 0,
            // ![filter.district, filter.powerstation].includes("all") &&
            // Boolean(filter.district && filter.powerstation),
            onSuccess(res) {
                if (res) setRowsCount(res?.data.count);
            },
            onError(err: any) {
                setErrFeedback({
                    severity: "error",
                    message: err.response.data.message,
                    show: true,
                });
            },
        }
    );

    const calcArrearsWithoutSurcharge = useMemo(
        () =>
            (row: MonthlyDefaulterBreakdown, formated = true) => {
                const { Total, Collection, PMCCollection } = row;

                const surcharge =
                    Total - ((Collection || 0) + (PMCCollection || 0));

                return formated
                    ? currencyFormatter.format(surcharge)
                    : surcharge;
            },
        [data?.data?.rows?.length]
    );

    const calcArrearsWithSurcharge = useMemo(
        () =>
            (row: MonthlyDefaulterBreakdown, formated = true) => {
                const {
                    Total,
                    AppliedSurcharge,
                    PaidSurcharge,
                    Collection,
                    PMCCollection,
                    AppliedSurchargeWhenNoCollection,
                    surchargeAdj,
                } = row;

                const appliedSurcharge = AppliedSurchargeWhenNoCollection ?? 0;
                const surcharge =
                    Total +
                    appliedSurcharge +
                    (AppliedSurcharge || 0) -
                    ((PaidSurcharge || 0) +
                        (surchargeAdj || 0) +
                        (Collection || 0) +
                        (PMCCollection || 0));

                return formated
                    ? currencyFormatter.format(surcharge)
                    : surcharge;
            },
        [data?.data?.rows?.length]
    );

    const createSummary = (cols: string[]) => {
        const summaryRow = ["Total"];

        if (
            !data ||
            !data.data ||
            !data.data.rows ||
            !Array.isArray(data.data.rows)
        ) {
            return summaryRow;
        }

        // arrearsWithoutSurcharge
        // arrearsWithSurcharge

        let arrearsWithoutSurcharge = 0;
        let arrearsWithSurcharge = 0;

        let totalAmount = 0;

        for (const row of data?.data.rows) {
            arrearsWithoutSurcharge += row.remain_bill_without_surcharge;
            arrearsWithSurcharge += row.remain_bill_with_surcharge;
        }

        for (const col of cols) {
            switch (col) {
                case "arrearsWithoutSurcharge":
                    summaryRow.push(
                        currencyFormatter.format(arrearsWithoutSurcharge)
                    );
                    break;

                case "arrearsWithSurcharge":
                    summaryRow.push(
                        currencyFormatter.format(arrearsWithSurcharge)
                    );
                    break;

                default:
                    summaryRow.push("");
            }
        }

        console.log(summaryRow, "summaryRow");

        return summaryRow;
    };

    /* ================= CLEAR ALL ================= */

    const handleClearAll = () => {
        // reset UI filters
        // reset applied filters
        setFilter(initialFilter);
        // notify child inputs
        setClearAll({
            district: true,
            village: true,
            powerstation: true,
            hamlet: true,
            search: true,
            connectionType: true,
        });

        // reset search trigger & pagination
        setChangeCount(0);
        setPagination({ page: 0, limit: 100 });

        setBillingMonth("");

        // turn flags off after render
        setTimeout(
            () =>
                setClearAll({
                    district: false,
                    village: false,
                    powerstation: false,
                    hamlet: false,
                    search: false,
                    connectionType: false,
                }),
            0
        );
    };
    useEffect(() => {
        if (
            filter.district &&
            filter.powerstation &&
            ![filter.district, filter.powerstation].includes("all")
        )
            return;

        setChangeCount(0);
    }, [filter.district, filter.powerstation]);

    useEffect(() => {
        if (billingMonth) {
            const [year, month] = billingMonth.split("-") ?? ["", ""];

            setFilter({
                ...filter,
                year,
                month: parseInt(month),
            });
        }
    }, [billingMonth]);

    return (
        <Box
            sx={{
                display: "flex",
                flexDirection: "column",
                // height: mdDown ? "auto" : `calc(100vh - 64px)`,
            }}
        >
            <Card
                elevation={0}
                sx={{
                    bgcolor: theme.palette.common.white,
                    p: 2,
                    mb: "1rem",
                }}
            >
                <Grid container>
                    <Grid item xs={12} md="auto">
                        <Typography variant="h5" color="GrayText">
                            Arrears Report
                        </Typography>
                    </Grid>
                </Grid>

                <Grid container spacing={1} mt={1}>
                    <MultiPermissionAuthorize ops={["READ DISTRICT"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/district"
                                label="District"
                                filter={filter}
                                setFilter={setFilter}
                                clearAll={Boolean(
                                    !filter.district ||
                                    filter.district === "all"
                                )}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ POWERSTATION"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                label="Powerstation"
                                api="/powerstation"
                                filter={filter}
                                setFilter={setFilter}
                                dep={filter.district}
                                params={{
                                    district:
                                        filter.district &&
                                        filter.district !== "all"
                                            ? filter.district
                                            : "",
                                }}
                                clearAll={Boolean(
                                    !filter.district ||
                                    filter.district === "all"
                                )}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ VILLAGE"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/village"
                                label="Village"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[filter.district, filter.powerstation]}
                                params={{
                                    district:
                                        filter.district &&
                                        filter.district !== "all"
                                            ? filter.district
                                            : "",

                                    powerstation:
                                        filter.powerstation &&
                                        filter.powerstation !== "all"
                                            ? filter.powerstation
                                            : "",
                                }}
                                clearAll={Boolean(
                                    !filter.district ||
                                    filter.district === "all"
                                )}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ HAMLET"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                api="/region/hamlet"
                                label="Hamlet"
                                filter={filter}
                                setFilter={setFilter}
                                dep={[filter.village, filter.powerstation]}
                                params={{
                                    village: filter.village,
                                    powerstation: filter.powerstation,
                                }}
                                clearAll={Boolean(
                                    !filter.village || filter.village === "all"
                                )}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <MultiPermissionAuthorize ops={["READ CONNECTION_TYPE"]}>
                        <Grid item xs={12} md={6} lg={4}>
                            <UpdatedSearchableInput
                                key={filter.connectionType}
                                label="Connection Type"
                                api="/connection"
                                filter={filter}
                                setFilter={setFilter}
                                params={{
                                    district: filter.district,
                                }}
                                multiple={true}
                                clearAll={clearAll.connectionType}
                            />
                        </Grid>
                    </MultiPermissionAuthorize>

                    <Grid item xs={12} md={6} lg={4}>
                        <TextField
                            size="small"
                            name="billingMonth"
                            label="Billing month"
                            type="month"
                            required
                            fullWidth
                            value={billingMonth}
                            onChange={(e) => setBillingMonth(e.target.value)}
                            inputProps={{ max: currentMonth, min: "2025-12" }}
                            InputLabelProps={{ shrink: true }}
                            InputProps={{
                                endAdornment: billingMonth ? (
                                    <IconButton
                                        size="small"
                                        onClick={() => setBillingMonth("")}
                                    >
                                        <CloseOutlined />
                                    </IconButton>
                                ) : null,
                            }}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <SearchBar
                            filter={filter}
                            setFilter={setFilter}
                            clear={clearAll.search}
                        />
                    </Grid>

                    <Grid item xs={12} md={6} lg={4}>
                        <Button
                            color="secondary"
                            variant="outlined"
                            onClick={() => {
                                setChangeCount((n) =>
                                    filter.search
                                        ? n + 1
                                        : [
                                                filter.district,
                                                filter.powerstation,
                                            ].some(
                                                (x) => x === "all" || x === ""
                                            )
                                          ? 0
                                          : n + 1
                                );
                                // setLoad(true);
                            }}
                            sx={{ height: "100%" }}
                            startIcon={<FilterAltOutlined />}
                            fullWidth
                        >
                            Search
                        </Button>
                    </Grid>

                    {/* CLEAR ALL */}
                    <Grid item xs={12} md={4}>
                        <Button
                            color="error"
                            variant="outlined"
                            startIcon={<FilterAltOffOutlined />}
                            onClick={handleClearAll}
                            sx={{
                                height: "100%",
                                minWidth: theme.spacing(20),
                            }}
                            fullWidth
                        >
                            Clear All
                        </Button>
                    </Grid>
                </Grid>
            </Card>

            <Box>
                {errFeedback.show &&
                    filter.month == "" &&
                    filter.year == "" && (
                        <Alert severity={errFeedback.severity as any}>
                            {errFeedback.message}
                        </Alert>
                    )}
                {changeCount === 0 ? (
                    <Alert severity="info" sx={{ mt: "1rem" }}>
                        Please select "District" and "PowerStation" or enter
                        "Meter No"
                    </Alert>
                ) : (
                    <BaseTable
                        headers={{
                            meterId: "ID",
                            customerName: "Name",
                            fatherName: "fatherName",
                            cnic: "Cnic",
                            mobileNumber: "mobileNumber",
                            meterNo: "meterNo",
                            districtName: "District",
                            districtId: "districtId",
                            powerstationName: "PowerStation",
                            villageId: "villageId",
                            villageName: "Village",
                            hamletName: "Hamlet",
                            hamletId: "hamletId",
                            arrearsWithoutSurcharge:
                                "Arrears Without Surcharge",
                            arrearsWithSurcharge: "Arrears With Surcharge",
                            connectionType: "connectionType",
                            details: "Details",
                        }}
                        defaultSelectedHeaders={[
                            "customerName",
                            "fatherName",
                            "cnic",
                            "mobileNumber",
                            "meterNo",
                            "districtName",
                            "powerstationName",
                            "villageName",
                            "hamletName",
                            "arrearsWithoutSurcharge",
                            "arrearsWithSurcharge",
                            "details",
                        ]}
                        data={data?.data.rows.map((row: any) => ({
                            ...row,
                            details: <ArrearsReportDetailNew row={row} />,
                            arrearsWithoutSurcharge: currencyFormatter.format(
                                row.remain_bill_without_surcharge
                            ),
                            arrearsWithSurcharge: currencyFormatter.format(
                                row.remain_bill_with_surcharge
                            ),
                            meterNo: `${row.meterNo}`,
                            connectionType: row.connectionType,
                        }))}
                        delEndPoint=""
                        feedback={feedback}
                        setFeedback={setFeedback}
                        rowsCount={rowsCount}
                        pagination={pagination}
                        setPagination={setPagination}
                        isLoading={isLoading}
                        permissions={{
                            edit: [""],
                            delete: [""],
                        }}
                        load={
                            (filter.search
                                ? filter.search !== ""
                                : Boolean(filter.district || filter.search) &&
                                  filter.district !== "all") && changeCount > 0
                        }
                        queryKey={[
                            "arrears",
                            pagination.page,
                            pagination.limit,
                            changeCount,
                        ]}
                        reportName="Arrears__List"
                        summaryRow={createSummary}
                        reportDetails={{
                            district: filter.district
                                ? filter.district
                                : undefined,
                            powerstation: filter.powerstation
                                ? filter.powerstation
                                : undefined,
                            village: filter.village
                                ? filter.village
                                : undefined,
                            hamlet: filter.hamlet ? filter.hamlet : undefined,
                        }}
                    />
                )}
            </Box>
        </Box>
    );
};

export default ArrearsReportNew;
